import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Typography from '@app/stories/Typography'
import Button from '@app/stories/Button'
import { formatPhoneNumberForProfile } from '@app/libs/formatters'
import { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useWhiteLabelingContext } from '@app/context/WhiteLabelingContext'
import { useMapEffects } from '@app/context/MapEffects'

// Style objects
const styles = {
  zeroCostBadge: {
    width: 'min-content',
    marginLeft: '-4px',
    transform: 'skew(-10deg)',
  },
  zeroCostText: {
    color: 'white',
    padding: '2px 8px',
    margin: '0px 0px 4px 0px',
    whiteSpace: 'nowrap',
    fontSize: '14px',
  },
  infoText: (color) => ({
    color: `${color}dd`,
    fontSize: '14px',
  }),
  expandButton: (color) => ({
    cursor: 'pointer',
    p: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: `${color}11`,
    },
  }),
  locationItem: (color) => ({
    p: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${color}22`,
      transform: 'translateX(4px)',
    },
  }),
  getStartedButton: (color) => ({
    height: '40px',
    margin: '4px',
    width: '120px',
    backgroundColor: color,
  }),
  getStartedText: {
    margin: '0px',
    whiteSpace: 'nowrap',
    color: 'white',
  },
}

// Utility functions
const normalizeText = (text) => {
  if (!text) return ''
  return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

const generateMapLink = (addressObj) => {
  if (!addressObj) return null

  const parts = []
  if (addressObj.address1) parts.push(addressObj.address1)
  if (addressObj.address2) parts.push(addressObj.address2)
  if (addressObj.city) parts.push(addressObj.city)
  if (addressObj.state) parts.push(addressObj.state)
  if (addressObj.zip) parts.push(addressObj.zip)

  const address = parts.join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

const formatAddress = (address) => {
  const addressParts = []
  if (address.address1) addressParts.push(normalizeText(address.address1))
  if (address.address2) addressParts.push(normalizeText(address.address2))

  const cityStateZip = []
  if (address.city) cityStateZip.push(normalizeText(address.city))
  if (address.state) cityStateZip.push(address.state?.toUpperCase())

  const formattedAddress = addressParts.join(', ')
  const formattedCityStateZip = cityStateZip.join(', ')

  return {
    formattedAddress,
    formattedCityStateZip,
    zip: address.zip,
  }
}

// Location item component
const LocationItem = ({ location, planColorPrimary }) => {
  const { formattedAddress, formattedCityStateZip, zip } = formatAddress(location)
  const mapUrl = generateMapLink(location)

  return (
    <Box
      sx={styles.locationItem(planColorPrimary)}
      onClick={() => mapUrl && window.open(mapUrl, '_blank')}
    >
      <Typography
        variant='body1'
        style={{
          ...styles.infoText(planColorPrimary),
          textDecoration: mapUrl ? 'underline' : 'none',
        }}
      >
        {formattedAddress}
        {formattedAddress && formattedCityStateZip && ', '}
        {formattedCityStateZip}
        {zip && (formattedCityStateZip ? ' ' : '') + zip}
      </Typography>
    </Box>
  )
}

// Locations section component
const LocationsSection = ({ locations, planColorPrimary }) => {
  const [locationsExpanded, setLocationsExpanded] = useState(false)

  return (
    <Box paddingLeft='4px'>
      <Box
        display='flex'
        alignItems='center'
        sx={styles.expandButton(planColorPrimary)}
        onClick={() => setLocationsExpanded(!locationsExpanded)}
      >
        <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
          <b>Locations ({locations.length}):</b>
        </Typography>
        {locationsExpanded ? (
          <ExpandLessIcon fontSize='small' sx={{ color: `${planColorPrimary}dd`, ml: 1 }} />
        ) : (
          <ExpandMoreIcon fontSize='small' sx={{ color: `${planColorPrimary}dd`, ml: 1 }} />
        )}
      </Box>

      {locationsExpanded && (
        <Box pl={2} mt={1}>
          {locations.map((location, index) => (
            <LocationItem key={index} location={location} planColorPrimary={planColorPrimary} />
          ))}
        </Box>
      )}
    </Box>
  )
}

// Single address component
const SingleLocation = ({ address, planColorPrimary }) => {
  const { formattedAddress, formattedCityStateZip, zip } = formatAddress(address)
  const mapUrl = generateMapLink(address)

  return (
    <Box
      sx={styles.locationItem(planColorPrimary)}
      onClick={() => mapUrl && window.open(mapUrl, '_blank')}
    >
      <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
        <b>Location:</b> {formattedAddress}
        {formattedAddress && formattedCityStateZip && ', '}
        {formattedCityStateZip}
        {zip && (formattedCityStateZip ? ' ' : '') + zip}
      </Typography>
    </Box>
  )
}

// Main component
const PlanAdminProviderSearchCard = ({
  data = { firstname: '', middlename: '', lastname: '', statesLicensed: [] },
  onClick = null,
  hideButton = false,
}) => {
  const {
    name,
    firstname,
    middlename,
    lastname,
    statesLicensed = [],
    npi,
    phone,
    geopoint,
    locations = [],
    specialty = [],
    zeroCostProvider,
    address,
    _rankingInfo = {},
    id,
  } = data

  const distance = _rankingInfo?.geoDistance
    ? parseInt(_rankingInfo.geoDistance / 1609.344, 10)?.toLocaleString()
    : null

  const { planColorPrimary } = useWhiteLabelingContext()
  const { handleHover, handleClick, clicked } = useMapEffects()

  const mapClick = () => {
    handleHover(false)
    if (clicked === id) {
      handleClick(false)
    } else {
      handleClick(true, id, data)
    }
  }

  return (
    <Box
      onMouseEnter={() => handleHover(true, id, data)}
      onMouseLeave={() => handleHover(null)}
      onMouseDown={mapClick}
      width='100%'
      padding='16px'
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      bgcolor={clicked === id ? `${planColorPrimary}22` : 'transparent'}
      sx={{
        '&:hover': {
          bgcolor:
            clicked === id
              ? `${planColorPrimary}22`
              : !clicked
                ? `${planColorPrimary}22`
                : 'transparent',
        },
      }}
    >
      <Box>
        {zeroCostProvider && (
          <Box sx={styles.zeroCostBadge} backgroundColor={planColorPrimary}>
            <Typography variant='h5' style={styles.zeroCostText}>
              Zero Cost Provider
            </Typography>
          </Box>
        )}
        <Box>
          <Typography
            variant='body1'
            weight='bold'
            style={{
              color: planColorPrimary,
              textTransform: 'capitalize',
            }}
          >
            {name ? (
              name.toLowerCase()
            ) : (
              <>
                {(firstname ? firstname + ' ' : '').toLowerCase()}
                {(middlename ? middlename + ' ' : '').toLowerCase()}
                {(lastname ? lastname : '').toLowerCase()}
              </>
            )}
          </Typography>
        </Box>
        <Box paddingLeft='4px'>
          {statesLicensed.length > 0 && (
            <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
              <b>States Licensed:</b> {statesLicensed.join(', ')}
            </Typography>
          )}
          {specialty.length > 0 && (
            <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
              <b>{specialty.length > 1 ? 'Specialties' : 'Specialty'}:</b>{' '}
              {specialty.map && specialty.join(', ')}
            </Typography>
          )}
          {npi && (
            <Typography
              variant='body1'
              style={{
                ...styles.infoText(planColorPrimary),
                textTransform: 'capitalize',
              }}
            >
              <b>NPI:</b> {npi}
            </Typography>
          )}
          {phone && (
            <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
              <b>Phone:</b> {formatPhoneNumberForProfile(phone)}
            </Typography>
          )}
          {distance && (
            <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
              Distance: {distance} miles
            </Typography>
          )}
        </Box>

        {locations && locations.length > 0 ? (
          <LocationsSection locations={locations} planColorPrimary={planColorPrimary} />
        ) : address ? (
          <SingleLocation address={address} planColorPrimary={planColorPrimary} />
        ) : (
          !geopoint && (
            <Typography variant='body1' style={styles.infoText(planColorPrimary)}>
              <b>Location:</b> Not specified
            </Typography>
          )
        )}
      </Box>
      <Box
        display='flex'
        justifyContent='space-between'
        height='100%'
        alignItems='center'
        sx={{
          flexDirection: {
            xs: 'row',
            sm: 'row',
            md: 'column',
            lg: 'column',
          },
        }}
        className='provider-search-card-button-container'
      >
        {!hideButton && (
          <Button
            variant='unstyled'
            style={styles.getStartedButton(planColorPrimary)}
            onClick={() => window?.Intercom('show')}
            className='provider-search-card-button'
          >
            <Typography variant='h5' weight='light' style={styles.getStartedText}>
              Get Started
            </Typography>
          </Button>
        )}
      </Box>
    </Box>
  )
}

PlanAdminProviderSearchCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    firstname: PropTypes.string,
    middlename: PropTypes.string,
    lastname: PropTypes.string,
    name: PropTypes.string,
    statesLicensed: PropTypes.array,
    npi: PropTypes.string,
    phone: PropTypes.string,
    geopoint: PropTypes.any,
    locations: PropTypes.array,
    specialty: PropTypes.array,
    zeroCostProvider: PropTypes.bool,
    address: PropTypes.object,
    _rankingInfo: PropTypes.object,
  }),
  onClick: PropTypes.func,
  hideButton: PropTypes.bool,
}

export default PlanAdminProviderSearchCard
